import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { UncontrolledPopover, PopoverBody } from 'reactstrap';
import { authenticatedHeadersHash } from '../../javascript/v2_authentication';
import vlog from '../../javascript/vlog';
import withV2HealthCheck from '../with_v2_health_check';

async function enrichPitchesWithPromoEntities(pitches) {
  if (!pitches || pitches.length === 0) {
    return pitches;
  }

  // Collect all unique promo entity IDs from the first pitched entity in each pitch
  const promoEntityIds = pitches
    .map((pitch) => pitch.pitched_promo_entity_ids?.[0])
    .filter((id) => id != null);

  if (promoEntityIds.length === 0) {
    vlog.warn('PublicPitchesWidget: No promo entity IDs found in pitches');
    return pitches;
  }

  vlog.info('PublicPitchesWidget: Fetching promo entities:', promoEntityIds);

  // Fetch promo entities using the new generic promo_entities API
  const { data: promoEntities, error: promoError } = await window.supabaseClient
    .from('promo_entities')
    .select('id, migrate_id, type, slug, name, headline, profile_image_url')
    .in('id', promoEntityIds);

  if (promoError) {
    vlog.error('PublicPitchesWidget: Error fetching promo entities:', promoError);
    return pitches; // Return pitches without enrichment rather than failing
  }

  vlog.info(`PublicPitchesWidget: Fetched ${promoEntities?.length || 0} promo entities`);

  // Create map for quick lookup
  const promoEntityMap = (promoEntities || []).reduce((map, entity) => ({
    ...map,
    [entity.id]: entity,
  }), {});

  // Enrich each pitch with its corresponding promo entity data
  return pitches.map((pitch) => {
    const promoEntityId = pitch.pitched_promo_entity_ids?.[0];
    const promoEntity = promoEntityMap[promoEntityId];

    if (!promoEntity) {
      vlog.warn(`PublicPitchesWidget: No promo entity found for pitch ${pitch.id}, entity ID: ${promoEntityId}`);
      return pitch;
    }

    return {
      ...pitch,
      promo_entity: promoEntity,
    };
  });
}

function PublicPitchesWidget({ limit, user_hashtags: userHashtags }) {
  const [pitches, setPitches] = useState([]);

  useEffect(() => {
    async function fetchRecentPitches() {
      // Fetch extra pitches to account for filtering out default avatars
      vlog.info('PublicPitchesWidget: Fetching recent pitches for immediate display');

      try {
        await window.initializeSupabaseClient();

        const { data: recentPitchData, error: recentError } = await window.supabaseClient
          .from('public_pitches')
          .select('*')
          .order('id', { ascending: false })
          .limit(limit * 2); // Fetch double to ensure we have enough after filtering

        if (recentError) {
          vlog.error('PublicPitchesWidget: Error fetching recent pitches:', recentError);
          return;
        }

        vlog.info(`PublicPitchesWidget: Fetched ${recentPitchData?.length || 0} recent pitches`);

        // Enrich and filter for cards with real profile images
        const enrichedRecent = await enrichPitchesWithPromoEntities(recentPitchData || []);
        const filteredPitches = enrichedRecent
          .filter((pitch) => pitch.promo_entity?.profile_image_url
                  && !pitch.promo_entity.profile_image_url.includes('dicebear'))
          .slice(0, limit);

        vlog.info(`PublicPitchesWidget: After filtering for real images: ${filteredPitches.length} pitches`);
        setPitches(filteredPitches);
      } catch (err) {
        vlog.error('PublicPitchesWidget: Error in fetchRecentPitches:', err);
      }
    }

    async function fetchSemanticPitches() {
      const startTime = performance.now();
      vlog.info('PublicPitchesWidget: Starting semantic search with limit:', limit);

      try {
        // Get reporter's V2 profile ID
        const profileId = await window.v2ProfileId();
        if (!profileId) {
          // Reporter not migrated to V2 yet - keep showing recent pitches
          vlog.info('PublicPitchesWidget: No V2 profile ID found, keeping recent pitches');
          return;
        }

        vlog.info('PublicPitchesWidget: Using V2 profile ID:', profileId);

        // Call semantic search API - fetch extra to account for filtering
        const params = new URLSearchParams({
          query_model_type: 'Id::Profile::MediaProfile',
          query_model_id: profileId,
          query_model_embedding_type: 'MatchFeed::AggregatedEmbedding',
          search_model_types: 'Enrich::PublicPitch',
          limit: (limit * 2).toString(), // Fetch double to ensure enough after filtering
          within_distance: 0.5,
          model_timestamp_within_hours: 730, // 1 month
          search_vector_column: 'text_embedding_3_large_256',
          distance_type: 'cosine',
          order_by: 'distance_asc',
        });

        const apiUrl = `${window.V2_URL}/match_feed/api/similarity_search?${params}`;
        vlog.info('PublicPitchesWidget: Calling semantic search API:', apiUrl);

        const apiStartTime = performance.now();
        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: authenticatedHeadersHash(),
        });
        const apiDuration = performance.now() - apiStartTime;

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        const data = await response.json();
        vlog.info(`PublicPitchesWidget: API response received in ${apiDuration.toFixed(0)}ms:`, data);

        // Extract pitch IDs from results
        const pitchIds = data.data?.results?.map((result) => result.model_id) || [];
        vlog.info('PublicPitchesWidget: Extracted pitch IDs:', pitchIds);

        if (pitchIds.length === 0) {
          // No semantic matches found
          vlog.info('PublicPitchesWidget: No semantic matches found');
          return;
        }

        // Hydrate full pitch data from Supabase
        await window.initializeSupabaseClient();

        const supabaseStartTime = performance.now();
        const { data: pitchData, error: supabaseError } = await window.supabaseClient
          .from('public_pitches')
          .select('*')
          .in('id', pitchIds);
        const supabaseDuration = performance.now() - supabaseStartTime;

        vlog.info(`PublicPitchesWidget: Supabase query completed in ${supabaseDuration.toFixed(0)}ms, `
                  + `returned ${pitchData?.length || 0} pitches`);

        if (supabaseError) {
          throw supabaseError;
        }

        // Enrich pitches with promo entity data for messaging links
        const enrichedSemanticPitches = await enrichPitchesWithPromoEntities(pitchData || []);

        // Filter for cards with real profile images (no dicebear defaults)
        const filteredSemanticPitches = enrichedSemanticPitches
          .filter((pitch) => pitch.promo_entity?.profile_image_url
                  && !pitch.promo_entity.profile_image_url.includes('dicebear'))
          .slice(0, limit);

        vlog.info(`PublicPitchesWidget: After filtering semantic results for real images: ${filteredSemanticPitches.length} pitches`);

        // Merge semantic results with recent pitches
        // If we got semantic results, use them first and fill remaining slots with recent
        if (filteredSemanticPitches.length > 0) {
          setPitches((currentPitches) => {
            // Take as many semantic results as we got
            const semanticCount = filteredSemanticPitches.length;
            // Fill remaining slots with recent pitches (avoid duplicates)
            const semanticIds = new Set(filteredSemanticPitches.map((p) => p.id));
            const recentFiller = currentPitches
              .filter((p) => !semanticIds.has(p.id))
              .slice(0, limit - semanticCount);

            const merged = [...filteredSemanticPitches, ...recentFiller].slice(0, limit);
            vlog.info(`PublicPitchesWidget: Swapped in ${semanticCount} semantic results, kept ${recentFiller.length} recent`);
            return merged;
          });
        } else {
          vlog.info('PublicPitchesWidget: No semantic results after filtering, keeping recent pitches');
        }

        const totalDuration = performance.now() - startTime;
        vlog.info(`PublicPitchesWidget: Semantic search completed in ${totalDuration.toFixed(0)}ms`);
      } catch (err) {
        vlog.error('PublicPitchesWidget: Error in semantic search:', err);
        // Keep showing recent pitches on error
      }
    }

    // Kick off both fetches - recent immediately, semantic in parallel
    fetchRecentPitches();
    fetchSemanticPitches();
  }, [limit]);

  // Hide widget if no results (show nothing while loading)
  if (pitches.length === 0) {
    return null;
  }

  const renderHeader = () => (
    <h2>
      Story Ideas
      {' '}
      <button
        id="public-pitches-help-icon"
        type="button"
        className="btn btn-link ps-0 pt-0"
        tabIndex="0"
      >
        <i className="fa-solid fa-circle-question" />
      </button>
      <UncontrolledPopover
        placement="auto"
        target="public-pitches-help-icon"
        trigger="hover focus"
      >
        <PopoverBody>
          Commentary relevant to you, from across the Qwoted network.
          You can adjust these suggestions via the tags in your profile at any time.
        </PopoverBody>
      </UncontrolledPopover>
    </h2>
  );

  const renderPitch = (pitch) => {
    const backPath = encodeURIComponent(window.location.pathname);

    // Construct messaging link based on promo entity type and V1 migrate_id
    let messagingLink = null;
    if (pitch.promo_entity && pitch.promo_entity.migrate_id) {
      const { migrate_id: migrateId, type } = pitch.promo_entity;

      // Different param names based on entity type
      if (type === 'Id::PromoEntity::Expert' || type === 'Id::PromoEntity::Company') {
        messagingLink = `/users/${window.CURRENT_USER_JSON.id}/multi_messages/new?source_id=${migrateId}&back_path=${backPath}`;
      } else if (type === 'Id::PromoEntity::Product') {
        messagingLink = `/users/${window.CURRENT_USER_JSON.id}/multi_messages/new?product_id=${migrateId}&back_path=${backPath}`;
      }
    }

    return (
      <div key={pitch.id} className="col-12 col-sm-6 col-md-6 col-lg-4 col-xl-6 col-xxl-4 pe-2 mb-3 d-flex">
        <div className="card mb-2 h-100 d-flex flex-column">
          <div className="card-header border-0 py-3 flex-grow-1 d-flex flex-column">
            {pitch.title && (
              <h5 className="fw-bold mb-2">{pitch.title}</h5>
            )}

            <p className="flex-grow-1">
              {pitch.text}
            </p>

            {messagingLink && (
              <a href={messagingLink}>
                <button
                  type="button"
                  className="btn btn-primary me-2"
                  style={{ padding: '0.25rem', paddingTop: '0.15rem', borderRadius: '5px' }}
                >
                  <i className="fa-solid fa-envelope me-1" style={{ verticalAlign: 'middle' }} />
                  <small>Contact this source</small>
                </button>
              </a>
            )}
          </div>

          {pitch.promo_entity && (
            <div className="card-footer bg-white">
              <div className="d-flex align-items-center mb-3">
                <img
                  src={pitch.promo_entity.profile_image_url}
                  alt={pitch.promo_entity.name}
                  className="rounded-circle me-3"
                  style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                />
                <div>
                  <a
                    href={`/sources/${pitch.promo_entity.migrate_id}`}
                    className="text-decoration-none fw-bold"
                  >
                    {pitch.promo_entity.name}
                  </a>
                  <br />
                  <p className="text-muted d-block my-0" style={{ height: '2.5em' }}>
                    {pitch.promo_entity.headline?.substring(0, 40)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStoryIdeasLink = () => {
    if (!userHashtags || userHashtags.length === 0) {
      return null;
    }

    const hashtagQuery = userHashtags.join(' ');
    const storyIdeasUrl = `/story_ideas?query=${encodeURIComponent(hashtagQuery)}`;

    return (
      <div style={{ float: 'left' }}>
        <a href={storyIdeasUrl} className="pe-2">
          See more in <u>STORY IDEAS</u>
        </a>
      </div>
    );
  };

  return (
    <>
      {renderHeader()}
      <div className="row gx-0 d-flex flex-wrap">
        {pitches.map((pitch) => renderPitch(pitch))}
      </div>
      {renderStoryIdeasLink()}
    </>
  );
}

PublicPitchesWidget.propTypes = {
  limit: PropTypes.number,
  user_hashtags: PropTypes.arrayOf(PropTypes.string),
};

PublicPitchesWidget.defaultProps = {
  limit: 3,
  user_hashtags: [],
};

export default withV2HealthCheck(PublicPitchesWidget, null);
